import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Shield,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Wrench,
  ClipboardList,
  AlertTriangle,
  Calendar,
  Gauge,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronRight,
  User,
  Building2,
  FileText,
  RefreshCw,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (value) => {
  if (!value) return "TZS 0";
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function VehicleDetailDialog({ vehicle, open, onOpenChange }) {
  const [warrantyStatus, setWarrantyStatus] = useState(null);
  const [repairHistory, setRepairHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRecord, setExpandedRecord] = useState(null);

  useEffect(() => {
    if (open && vehicle) {
      fetchVehicleDetails();
    }
  }, [open, vehicle]);

  const fetchVehicleDetails = async () => {
    setLoading(true);
    try {
      const [warrantyRes, historyRes] = await Promise.all([
        apiClient.get(`/vehicles/${vehicle.id}/warranty-status`),
        apiClient.get(`/vehicles/${vehicle.id}/repair-history`)
      ]);
      setWarrantyStatus(warrantyRes.data);
      setRepairHistory(historyRes.data);
    } catch (error) {
      console.error("Failed to fetch vehicle details:", error);
      toast.error("Failed to fetch vehicle details");
    } finally {
      setLoading(false);
    }
  };

  const getWarrantyIcon = () => {
    if (!warrantyStatus) return ShieldAlert;
    if (warrantyStatus.is_under_warranty) return ShieldCheck;
    return ShieldX;
  };

  const getWarrantyColor = () => {
    if (!warrantyStatus) return "text-muted-foreground";
    if (warrantyStatus.is_under_warranty) return "text-green-400";
    return "text-red-400";
  };

  const getSeverityBadge = (severity) => {
    const config = {
      high: { className: "bg-red-500/20 text-red-400 border-red-500/30", label: "Recurring" },
      medium: { className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Multiple" },
      low: { className: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Single" }
    };
    return config[severity] || config.low;
  };

  const getWarrantyClaimBadge = (status) => {
    const config = {
      approved: { className: "bg-green-500/20 text-green-400 border-green-500/30", label: "Approved" },
      denied: { className: "bg-red-500/20 text-red-400 border-red-500/30", label: "Denied" },
      pending: { className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Pending" }
    };
    return config[status] || { className: "bg-gray-500/20 text-gray-400 border-gray-500/30", label: status };
  };

  const WarrantyIcon = getWarrantyIcon();

  if (!vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-border pb-3">
          <DialogTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            VEHICLE DETAILS - {vehicle.registration_number}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-primary animate-spin" />
            <span className="ml-2 font-mono text-sm text-muted-foreground">Loading details...</span>
          </div>
        ) : (
          <Tabs defaultValue="warranty" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3 bg-background border border-border rounded-sm">
              <TabsTrigger value="warranty" className="font-heading text-xs data-[state=active]:bg-primary/10">
                WARRANTY
              </TabsTrigger>
              <TabsTrigger value="history" className="font-heading text-xs data-[state=active]:bg-primary/10">
                REPAIR HISTORY
              </TabsTrigger>
              <TabsTrigger value="recurring" className="font-heading text-xs data-[state=active]:bg-primary/10">
                RECURRING ISSUES
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              {/* WARRANTY TAB */}
              <TabsContent value="warranty" className="mt-0 space-y-4">
                {/* Warranty Status Card */}
                <Card className={`border-2 ${warrantyStatus?.is_under_warranty ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"} rounded-sm`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${warrantyStatus?.is_under_warranty ? "bg-green-500/20" : "bg-red-500/20"}`}>
                          <WarrantyIcon className={`w-6 h-6 ${getWarrantyColor()}`} />
                        </div>
                        <div>
                          <p className={`font-heading text-lg ${getWarrantyColor()}`}>
                            {warrantyStatus?.is_under_warranty ? "UNDER WARRANTY" : "WARRANTY EXPIRED"}
                          </p>
                          <p className="text-xs font-mono text-muted-foreground">
                            {vehicle.make} {vehicle.model}
                          </p>
                        </div>
                      </div>
                      {warrantyStatus?.is_under_warranty && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-mono text-xs">
                          PROTECTED
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Warranty Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-primary" />
                        <p className="text-xs font-mono text-muted-foreground uppercase">Purchase Date</p>
                      </div>
                      <p className="font-heading text-lg text-foreground">
                        {warrantyStatus?.purchase_date || "Not Recorded"}
                      </p>
                      {warrantyStatus?.warranty_end_date && (
                        <p className="text-xs font-mono text-muted-foreground mt-1">
                          Warranty ends: {warrantyStatus.warranty_end_date}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <p className="text-xs font-mono text-muted-foreground uppercase">Time Remaining</p>
                      </div>
                      <p className="font-heading text-lg text-foreground">
                        {warrantyStatus?.days_remaining > 0 ? `${warrantyStatus.days_remaining} days` : "Expired"}
                      </p>
                      <p className="text-xs font-mono text-muted-foreground mt-1">
                        {warrantyStatus?.warranty_years} year warranty
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Gauge className="w-4 h-4 text-primary" />
                        <p className="text-xs font-mono text-muted-foreground uppercase">Mileage Status</p>
                      </div>
                      <p className="font-heading text-lg text-foreground">
                        {warrantyStatus?.current_mileage?.toLocaleString()} km
                      </p>
                      <p className="text-xs font-mono text-muted-foreground mt-1">
                        Limit: {warrantyStatus?.warranty_mileage_limit?.toLocaleString()} km
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        <p className="text-xs font-mono text-muted-foreground uppercase">Mileage Remaining</p>
                      </div>
                      <p className="font-heading text-lg text-foreground">
                        {warrantyStatus?.mileage_remaining > 0 ? `${warrantyStatus.mileage_remaining.toLocaleString()} km` : "Exceeded"}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Usage Progress Bars */}
                <Card className="bg-card border-border rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-sm tracking-wide">WARRANTY USAGE</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-xs font-mono mb-1">
                        <span className="text-muted-foreground">Mileage Used</span>
                        <span className="text-foreground">{warrantyStatus?.warranty_percentage_used?.mileage || 0}%</span>
                      </div>
                      <Progress 
                        value={warrantyStatus?.warranty_percentage_used?.mileage || 0} 
                        className="h-2"
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs font-mono mb-1">
                        <span className="text-muted-foreground">Time Used</span>
                        <span className="text-foreground">{warrantyStatus?.warranty_percentage_used?.time || 0}%</span>
                      </div>
                      <Progress 
                        value={warrantyStatus?.warranty_percentage_used?.time || 0} 
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Eligibility Notes */}
                {warrantyStatus?.eligibility_notes?.length > 0 && (
                  <Card className="bg-card border-border rounded-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        WARRANTY NOTES
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {warrantyStatus.eligibility_notes.map((note, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm font-mono text-muted-foreground">
                            <ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            {note}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* REPAIR HISTORY TAB */}
              <TabsContent value="history" className="mt-0 space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-3">
                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-3 text-center">
                      <p className="text-xl font-heading font-bold text-primary">
                        {repairHistory?.summary?.total_records || 0}
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground">TOTAL RECORDS</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-3 text-center">
                      <p className="text-xl font-heading font-bold text-yellow-400">
                        {repairHistory?.summary?.total_repairs || 0}
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground">REPAIRS</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-3 text-center">
                      <p className="text-xl font-heading font-bold text-blue-400">
                        {repairHistory?.summary?.total_services || 0}
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground">SERVICES</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border rounded-sm">
                    <CardContent className="p-3 text-center">
                      <p className="text-lg font-heading font-bold text-foreground">
                        {formatCurrency(repairHistory?.summary?.total_cost || 0).replace("TZS", "").trim()}
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground">TOTAL COST (TZS)</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Warranty Claims Summary */}
                {repairHistory?.summary?.warranty_claims?.total > 0 && (
                  <Card className="bg-green-500/5 border-green-500/30 rounded-sm">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-sm font-heading text-green-400">WARRANTY CLAIMS</p>
                          <p className="text-xs font-mono text-green-400/70">
                            {repairHistory.summary.warranty_claims.approved} approved, {repairHistory.summary.warranty_claims.pending} pending
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-heading text-green-400">
                          {formatCurrency(repairHistory.summary.warranty_claims.savings)}
                        </p>
                        <p className="text-[10px] font-mono text-green-400/70">SAVINGS</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Records List */}
                <div className="space-y-2">
                  {repairHistory?.records?.map((record) => (
                    <Collapsible
                      key={record.id}
                      open={expandedRecord === record.id}
                      onOpenChange={(open) => setExpandedRecord(open ? record.id : null)}
                    >
                      <Card className="bg-card border-border rounded-sm overflow-hidden">
                        <CollapsibleTrigger className="w-full">
                          <CardContent className="p-3 flex items-center justify-between hover:bg-accent/50 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${record.type === "repair_order" ? "bg-yellow-500/20" : "bg-blue-500/20"}`}>
                                {record.type === "repair_order" ? (
                                  <ClipboardList className="w-4 h-4 text-yellow-400" />
                                ) : (
                                  <Wrench className="w-4 h-4 text-blue-400" />
                                )}
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-foreground">
                                  {record.type === "repair_order" ? record.repair_type : record.service_type}
                                </p>
                                <p className="text-xs font-mono text-muted-foreground">
                                  {record.date} • {record.issue_category || "General"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {record.is_warranty_claim && (
                                <Badge className={`${getWarrantyClaimBadge(record.warranty_claim_status).className} font-mono text-[10px]`}>
                                  WARRANTY
                                </Badge>
                              )}
                              <p className="text-sm font-mono text-foreground">
                                {formatCurrency(record.cost)}
                              </p>
                              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedRecord === record.id ? "rotate-180" : ""}`} />
                            </div>
                          </CardContent>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="px-3 pb-3 pt-0 border-t border-border bg-accent/30">
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div>
                                <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Description</p>
                                <p className="text-xs text-foreground">{record.description || record.reported_issue || "-"}</p>
                              </div>
                              {record.root_cause && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Root Cause</p>
                                  <p className="text-xs text-foreground">{record.root_cause}</p>
                                </div>
                              )}
                              {record.parts_replaced?.length > 0 && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Parts Replaced</p>
                                  <div className="flex flex-wrap gap-1">
                                    {record.parts_replaced.map((part, idx) => (
                                      <Badge key={idx} variant="outline" className="font-mono text-[10px]">{part}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {record.labor_hours && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Labor Hours</p>
                                  <p className="text-xs text-foreground">{record.labor_hours} hours</p>
                                </div>
                              )}
                              {record.workshop_name && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Workshop</p>
                                  <p className="text-xs text-foreground flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {record.workshop_name}
                                  </p>
                                </div>
                              )}
                              {record.assigned_mechanic && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Mechanic</p>
                                  <p className="text-xs text-foreground flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {record.assigned_mechanic}
                                  </p>
                                </div>
                              )}
                              {record.mileage_at_service && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Mileage at Service</p>
                                  <p className="text-xs text-foreground">{record.mileage_at_service?.toLocaleString()} km</p>
                                </div>
                              )}
                              {record.mileage_at_repair && (
                                <div>
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Mileage at Repair</p>
                                  <p className="text-xs text-foreground">{record.mileage_at_repair?.toLocaleString()} km</p>
                                </div>
                              )}
                              {record.recommendations && (
                                <div className="col-span-2">
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Recommendations</p>
                                  <p className="text-xs text-foreground">{record.recommendations}</p>
                                </div>
                              )}
                              {record.notes && (
                                <div className="col-span-2">
                                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Notes</p>
                                  <p className="text-xs text-foreground">{record.notes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  ))}
                  {(!repairHistory?.records || repairHistory.records.length === 0) && (
                    <div className="text-center py-8">
                      <Wrench className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-mono text-muted-foreground">No repair or service history found</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* RECURRING ISSUES TAB */}
              <TabsContent value="recurring" className="mt-0 space-y-4">
                {repairHistory?.recurring_issues?.some(i => i.is_recurring) ? (
                  <>
                    <Card className="bg-yellow-500/10 border-yellow-500/30 rounded-sm">
                      <CardContent className="p-4 flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-yellow-400" />
                        <div>
                          <p className="text-sm font-heading text-yellow-400">RECURRING ISSUES DETECTED</p>
                          <p className="text-xs font-mono text-yellow-400/70">
                            The following issue categories have occurred multiple times on this vehicle
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="font-heading text-xs">ISSUE CATEGORY</TableHead>
                          <TableHead className="font-heading text-xs">OCCURRENCES</TableHead>
                          <TableHead className="font-heading text-xs">TOTAL COST</TableHead>
                          <TableHead className="font-heading text-xs">STATUS</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {repairHistory.recurring_issues
                          .filter(issue => issue.occurrence_count > 0)
                          .map((issue, idx) => {
                            const badge = getSeverityBadge(issue.severity);
                            return (
                              <TableRow key={idx} className="border-border">
                                <TableCell className="font-mono text-sm">{issue.category}</TableCell>
                                <TableCell className="font-mono text-sm">{issue.occurrence_count}x</TableCell>
                                <TableCell className="font-mono text-sm">{formatCurrency(issue.total_cost)}</TableCell>
                                <TableCell>
                                  {issue.is_recurring ? (
                                    <Badge className={`${badge.className} font-mono text-xs border`}>
                                      {badge.label}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs font-mono text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>

                    {/* Recommendation */}
                    <Card className="bg-card border-border rounded-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-sm tracking-wide">RECOMMENDATIONS</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {repairHistory.recurring_issues
                          .filter(i => i.occurrence_count >= 3)
                          .map((issue, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-sm">
                              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                              <p className="font-mono text-muted-foreground">
                                <span className="text-foreground font-medium">{issue.category}</span> has occurred {issue.occurrence_count} times. 
                                Consider a comprehensive inspection or component replacement to prevent future failures.
                              </p>
                            </div>
                          ))}
                        {repairHistory.recurring_issues
                          .filter(i => i.occurrence_count === 2)
                          .map((issue, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-sm">
                              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                              <p className="font-mono text-muted-foreground">
                                <span className="text-foreground font-medium">{issue.category}</span> has occurred twice. 
                                Monitor closely during next service.
                              </p>
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <ShieldCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="font-heading text-lg text-foreground">NO RECURRING ISSUES</p>
                    <p className="text-sm font-mono text-muted-foreground mt-1">
                      This vehicle has no recurring repair or service issues detected
                    </p>
                  </div>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
